import { Button, Divider, Filter, Tag } from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import {
  type MRT_ColumnDef,
  MantineReactTable,
  useLazyTable,
} from '@vrplatform/table';
import { day, formatCurrency, toTitleCase } from '@vrplatform/utils';
import {
  Box,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Text,
  Title,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { Fragment, useMemo, useState } from 'react';
import { useEmbedViewMode } from '../../providers';
import { GuestBalancesTableFilter } from './GuestBalancesTableFilter';
import {
  type GuestBalancesOccupancyStatus,
  useGuestBalancesTableFilter,
} from './useGuestBalancesTableFilter';
import { guestBalancesOccupancyStatusCards } from './GuestBalancesTable.utils';
import {
  type GuestBalancesTableQueryRow,
  type GuestBalancesTableQuerySorting,
  useGuestBalancesTableQuery,
} from './useGuestBalancesTableQuery';

export const STORYBOOK_GUEST_BALANCES_AGGREGATE_QUERY_KEY =
  'guest-balances-aggregate';

export const GuestBalancesTable = ({
  onOpenDeposit,
  onOpenReservation,
}: {
  onOpenDeposit?: (transactionId: string) => void;
  onOpenReservation?: (reservationId: string) => void;
}) => {
  const { filter } = useGuestBalancesTableFilter();

  return (
    <Fragment key={filter.occupancyStatus}>
      <Cards />
      <Table
        onOpenDeposit={onOpenDeposit}
        onOpenReservation={onOpenReservation}
      />
    </Fragment>
  );
};

const Cards = () => {
  const queryData = useAggregateQuery();

  return (
    <Box
      mb="md"
      sx={{
        containerType: 'inline-size',
      }}
    >
      <SimpleGrid
        sx={{
          maxWidth: '2000px',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '1rem',
          '@container (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
          '@container (max-width: 800px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          '@container (max-width: 600px)': {
            gridTemplateColumns: 'repeat(1, 1fr)',
          },
        }}
      >
        {guestBalancesOccupancyStatusCards.map((card) => (
          <OccupancyStatusCard
            key={card.type}
            title={card.title}
            icon={card.icon}
            type={card.type}
            description={card.description}
            queryData={queryData}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};

function useSorting() {
  const [sorting, setSorting] = useState<GuestBalancesTableQuerySorting | null>(
    null
  );

  return {
    sorting,
    setSorting,
  };
}

const Table = ({
  onOpenDeposit,
  onOpenReservation,
}: {
  onOpenDeposit?: (transactionId: string) => void;
  onOpenReservation?: (reservationId: string) => void;
}) => {
  const { isEmbed } = useEmbedViewMode();
  const [{ currency }] = useTeam();
  const { sorting, setSorting } = useSorting();
  const queryData = useGuestBalancesTableQuery(sorting || 'checkOut');
  const rows = queryData.data?.rows ?? [];

  const columns = useMemo<MRT_ColumnDef<GuestBalancesTableQueryRow>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        grow: true,
        Cell: (props) => {
          const row = props.row.original;

          if (row.type === 'status') {
            return (
              <Group>
                <Text fw="bold">{row.name}</Text>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.neutral[3]}`,
                    borderRadius: theme.radius.sm,
                    paddingInline: rem(5),
                  })}
                >
                  <Text span c="neutral" size="xs">
                    Listings
                  </Text>
                </Box>
              </Group>
            );
          }

          if (row.type === 'listing') {
            return <Text fw={500}>{row.name}</Text>;
          }

          if (row.type === 'reservation') {
            if (row.bookingStatus === 'unassigned') {
              return (
                <Text c={row.transactionDescription ? undefined : 'red'}>
                  {row.transactionDescription ||
                    'Missing transaction description'}
                </Text>
              );
            }

            return (
              <Box>
                <Text span display="block">
                  {row.guestName}
                </Text>
                {row.confirmationCode && (
                  <Text span display="block" size="xs" c="gray">
                    {row.confirmationCode}
                    {row.bookingPlatform &&
                      `, ${toTitleCase(row.bookingPlatform)}`}
                  </Text>
                )}
              </Box>
            );
          }

          return null;
        },
      },
      {
        header: 'Dates',
        grow: true,
        maxSize: 200,
        Cell: (props) => {
          const row = props.row.original;

          if (row.type === 'reservation') {
            if (row.bookingStatus === 'unassigned') {
              return (
                <Text c={row.transactionDate ? undefined : 'red'}>
                  {row.transactionDate
                    ? day(row.transactionDate).format('MMM D, YYYY')
                    : 'Missing transaction date'}
                </Text>
              );
            }

            const getFormat = (showYear: boolean) =>
              showYear ? 'DD. MMM. YYYY' : 'DD. MMM';

            const checkIn = day(row.checkIn);
            const checkOut = day(row.checkOut);
            const bookedAt = day(row.bookedAt);
            const cancelledAt = day(row.cancelledAt);

            const checkInDifferentYear = checkIn.year !== checkOut.year;
            const bookedAtDifferentYear = bookedAt.year !== checkIn.year;
            const cancelledAtDifferentYear = cancelledAt.year !== checkIn.year;

            return (
              <Box w={300}>
                <Text span display="block">
                  {`${day(row.checkIn).format(getFormat(checkInDifferentYear))} - ${day(row.checkOut).format(getFormat(true))}`}
                </Text>
                <Text span display="block" size="xs" c="gray">
                  {row.nights} nights
                  {row.bookedAt
                    ? `, on ${day(row.bookedAt).format(getFormat(bookedAtDifferentYear))}`
                    : ''}
                  {row.cancelledAt
                    ? `, cancelled on ${day(row.cancelledAt).format(getFormat(cancelledAtDifferentYear))}`
                    : ''}
                </Text>
              </Box>
            );
          }

          return null;
        },
      },
      {
        header: 'Charges',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) =>
          formatCurrency(row.chargesCentTotal / 100, currency),
      },
      {
        header: 'Payments',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) =>
          formatCurrency(row.paymentsCentTotal / 100, currency),
      },
      {
        header: 'Amount',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight: props.row.depth !== 2 ? 'bold' : 'normal',
          }),
        }),
        accessorFn: (row) => formatCurrency(row.centTotal / 100, currency),
      },
    ],
    [currency]
  );

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
      onRowClick: {
        handler: (row) => {
          if (
            !isEmbed &&
            row.original.type === 'reservation' &&
            row.original.id
          ) {
            if (row.original.bookingStatus === 'unassigned') {
              onOpenDeposit?.(row.original.id);
              return;
            }

            onOpenReservation?.(row.original.id);
          }
        },
        disabled: (row) =>
          isEmbed ||
          row.original.type !== 'reservation' ||
          (row.original.bookingStatus === 'unassigned'
            ? !onOpenDeposit
            : !onOpenReservation),
      },
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      defaultExpanded: true,
      allowExpandingAll: false,
    },
    styles: {
      row: (props, theme) => {
        const depth = 2 - props.row.depth;

        if (depth) {
          return {
            backgroundColor: `${theme.colors.neutral[depth]}!important`,
          };
        }

        return {};
      },
    },
    virtualization: {
      enableRowVirtualization: true,
      overscan: 30,
    },
  });

  return (
    <>
      <Group justify="space-between">
        <GuestBalancesTableFilter />

        <Group>
          <Sort
            sorting={sorting}
            setSorting={(value) => {
              setSorting(value);
            }}
          />
          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>
      <Divider mt="sm" />
      <MantineReactTable table={table} />
    </>
  );
};

const Sort = ({
  sorting,
  setSorting,
}: {
  sorting: GuestBalancesTableQuerySorting | null;
  setSorting: (sorting: GuestBalancesTableQuerySorting | null) => void;
}) => {
  const options = useMemo<SelectItem<GuestBalancesTableQuerySorting>[]>(
    () => [
      {
        label: 'Check-In',
        value: 'checkIn',
      },
      {
        label: 'Check-Out',
        value: 'checkOut',
      },
      {
        label: 'Guest Name',
        value: 'guestName',
      },
      {
        label: 'Amount',
        value: 'centTotal',
      },
    ],
    []
  );

  const value = options.find((option) => option.value === sorting) ?? null;

  return (
    <Filter.Select
      label="Sort by"
      type="single"
      value={value}
      data={{ options }}
      setValue={(selected) => setSorting(selected?.value ?? null)}
    />
  );
};

function useAggregateQuery() {
  const { filter } = useGuestBalancesTableFilter();

  return useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET('/reports/guest-balances-aggregated', {
        params: {
          query: {
            date: args.endDate,
            channels: args.channels?.join(',') ?? undefined,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return result.data;
    },
    {
      queryGroup: 'reports',
      uniqueQueryKey: 'guest-balances-report-aggregated',
      storybookQueryKey: STORYBOOK_GUEST_BALANCES_AGGREGATE_QUERY_KEY,
      queryKeys: ['journalEntries', 'reports'],
      variables: {
        endDate: filter.endDate,
        channels: filter.channels,
      },
      keepPreviousData: true,
    }
  );
}

const OccupancyStatusCard = ({
  title,
  description,
  icon,
  type,
  queryData,
}: {
  title: string;
  description: string;
  icon: IconDefinition;
  type: GuestBalancesOccupancyStatus;
  queryData: ReturnType<typeof useAggregateQuery>;
}) => {
  const theme = useMantineTheme();
  const { filter, setOccupancyStatus } = useGuestBalancesTableFilter();
  const isActive = filter.occupancyStatus === type;
  const [{ currency }] = useTeam();
  const centTotal = queryData.data?.[type] ?? 0;
  const showWarningIcon =
    (type === 'cancelled' || type === 'departed') && centTotal !== 0;

  return (
    <Paper
      p="sm"
      radius="md"
      component="button"
      onClick={() => setOccupancyStatus(type)}
      sx={(mantineTheme) => ({
        cursor: 'pointer',
        minHeight: 90,
        border: `1px solid ${
          isActive
            ? mantineTheme.colors[mantineTheme.primaryColor][5]
            : mantineTheme.colors.gray[3]
        }`,
        backgroundColor: isActive
          ? mantineTheme.colors[mantineTheme.primaryColor][0]
          : mantineTheme.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        height: 'calc(100% - 3px)',
        justifyContent: 'space-between',
        boxShadow: isActive
          ? '0px 1px 2px 0px rgba(42, 59, 81, 0.12), 0px 0px 0px 1px rgba(18, 55, 105, 0.08)'
          : mantineTheme.shadows.xs,
      })}
    >
      <Group
        justify="space-between"
        align="center"
        gap="xs"
        wrap="nowrap"
        w="100%"
        mb={rem(8)}
      >
        <Icon
          icon={icon}
          color={(mantineTheme) =>
            isActive
              ? mantineTheme.colors[mantineTheme.primaryColor][6]
              : mantineTheme.colors.neutral[4]
          }
          size={22}
        />
        {queryData.isLoading ? (
          <Skeleton h={25} w={25} radius="xl" />
        ) : (
          <Tag
            leftIcon={showWarningIcon ? 'AlertCircleIcon' : undefined}
            color={theme.colors.neutral[5]}
            leftIconColor={theme.colors.red[6]}
            backgroundColor="transparent"
          >
            {formatCurrency(centTotal / 100, currency)}
          </Tag>
        )}
      </Group>
      <Title
        order={3}
        ta="left"
        size="md"
        mb={rem(4)}
        sx={(mantineTheme) => ({
          color: isActive
            ? mantineTheme.colors[mantineTheme.primaryColor][6]
            : mantineTheme.colors.neutral[6],
        })}
      >
        {title}
      </Title>
      <Text span size="xs" c="gray" ta="left">
        {description}
      </Text>
    </Paper>
  );
};
